import React, { useEffect } from 'react';
import { useUsageLimits } from '../../context/UsageLimitsContext';
import { useTheme } from '../../context/ThemeContext';

/**
 * UsageWarningBanner - Shows usage limit warnings (SAT-110)
 *
 * Features:
 * - Warning toast at 80% threshold
 * - Critical warning at 95%
 * - Exceeded limit alert
 * - Dismissible warnings
 */
function UsageWarningBanner() {
  // Admin-only feature: usage limit warnings hidden from regular users
  return null;

  // eslint-disable-next-line no-unreachable
  const { warnings, dismissWarning, fetchUsageFromAPI, isBlocked } = useUsageLimits();
  const { isDark } = useTheme();

  useEffect(() => {
    fetchUsageFromAPI();
    const interval = setInterval(fetchUsageFromAPI, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchUsageFromAPI]);

  if (warnings.length === 0) return null;

  const getWarningStyles = (type) => {
    switch (type) {
      case 'exceeded':
        return {
          bg: isDark ? 'bg-red-900/90' : 'bg-red-100',
          border: 'border-red-500',
          text: isDark ? 'text-red-200' : 'text-red-800',
          icon: (
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
        };
      case 'critical':
        return {
          bg: isDark ? 'bg-orange-900/90' : 'bg-orange-100',
          border: 'border-orange-500',
          text: isDark ? 'text-orange-200' : 'text-orange-800',
          icon: (
            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
      case 'warning':
      default:
        return {
          bg: isDark ? 'bg-yellow-900/90' : 'bg-yellow-100',
          border: 'border-yellow-500',
          text: isDark ? 'text-yellow-200' : 'text-yellow-800',
          icon: (
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        };
    }
  };

  // Show blocked message if hard limit enabled
  const blocked = isBlocked();

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      {blocked && (
        <div className={`p-4 rounded-lg border-l-4 shadow-lg ${isDark ? 'bg-red-900/95' : 'bg-red-50'} border-red-600`}>
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <div>
              <p className={`font-semibold ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                Usage Limit Exceeded
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                You've reached your usage limit. Further requests are blocked until limits reset.
              </p>
            </div>
          </div>
        </div>
      )}

      {warnings.map((warning) => {
        const styles = getWarningStyles(warning.type);
        return (
          <div
            key={warning.id}
            className={`p-3 rounded-lg border-l-4 shadow-lg animate-slide-in ${styles.bg} ${styles.border}`}
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">{styles.icon}</div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${styles.text}`}>
                  {warning.message}
                </p>
                {/* Progress bar */}
                <div className="mt-2">
                  <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className={`h-full ${
                        warning.type === 'exceeded' ? 'bg-red-500' :
                        warning.type === 'critical' ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(warning.percentage, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={() => dismissWarning(warning.id)}
                className={`shrink-0 p-1 rounded-full hover:bg-black/10 transition-colors ${styles.text}`}
                title="Dismiss"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default UsageWarningBanner;
